import { featureFlags } from '@seen/core/featureFlags'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { getReferenceString } from '@seen/core/references'
import { InsertCurrentConditionsButtonNode as InsertCurrentConditionsButtonSchema } from '@seen/core/tiptap/schema'
import { Button } from '@seen/ui'
import { IconClipboardPlus, IconLoader2 } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { type JSX, memo, useCallback, useContext, useState } from 'react'
import { toast } from 'sonner'
import {
  noteButtonClass,
  noteButtonLabelClass,
} from '#src/components/rich-text-editor/noteButtonClass.ts'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useEncounterDiagnoses } from '#src/hooks/useEncounterDiagnoses.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { fetchOrderedCurrentConditions } from '#src/hooks/useOrderedConditions.ts'

export const InsertCurrentConditionsButtonNode =
  InsertCurrentConditionsButtonSchema.extend({
    addNodeView() {
      return ReactNodeViewRenderer(InsertCurrentConditionsButtonNodeComponent)
    },
  })

function InsertCurrentConditionsButtonNodeComponent(
  props: NodeViewProps
): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <InsertCurrentConditionsButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const InsertCurrentConditionsButtonNodeComponentInner = memo(
  function InsertCurrentConditionsButtonNodeComponentInner({
    editor,
    deleteNode,
    getPos,
  }: NodeViewProps): JSX.Element {
    const medplum = useMedplum()
    const facesheetImprovements = useFeatureFlag(
      featureFlags.facesheetImprovements
    )
    const queryClient = useQueryClient()
    const participantReference = useOptionalParticipantReference()
    const compositionReferenceString = useContext(CompositionContext)
    const { addConditions } = useEncounterDiagnoses(compositionReferenceString)
    const getPosRef = useCurrentRef(getPos)
    const [isLoading, setIsLoading] = useState(false)

    const handleClick = useCallback(async () => {
      if (!participantReference) {
        toast.error('No participant context available')
        return
      }

      setIsLoading(true)

      try {
        const sortedConditions = await fetchOrderedCurrentConditions(
          medplum,
          participantReference,
          { manualOrder: !facesheetImprovements }
        )

        const pos = getPosRef.current()
        if (pos === undefined) {
          return
        }

        if (sortedConditions.length === 0) {
          deleteNode()
          editor
            .chain()
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text: 'No current conditions',
                },
              ],
            })
            .focus()
            .run()
          return
        }

        // Prime per-id cache so each ConditionMention's `useRead` is a cache hit.
        for (const condition of sortedConditions) {
          queryClient.setQueryData(
            cacheKeys.readExtended('Condition', condition.id, Diagnosis.name),
            condition
          )
        }

        // Fire the encounter linkage before deleteNode() unmounts the component
        // so the optimistic state update + mutation onError fire while mounted.
        addConditions(
          sortedConditions.map((c) => getReferenceString(c.reference))
        )

        const bulletListContent = {
          type: 'bulletList',
          content: sortedConditions.map((condition) => ({
            type: 'listItem',
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'mention',
                    attrs: {
                      reference: getReferenceString(condition.reference),
                      display: `@${condition.title}`,
                    },
                  },
                ],
              },
            ],
          })),
        }

        deleteNode()
        editor.chain().insertContentAt(pos, bulletListContent).focus().run()
      } catch (_error) {
        toast.error('Failed to fetch conditions')
        setIsLoading(false)
      }
    }, [
      medplum,
      facesheetImprovements,
      queryClient,
      participantReference,
      addConditions,
      getPosRef,
      editor,
      deleteNode,
    ])

    const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className={noteButtonClass(inlineCapture)}
        disabled={!editor.isEditable || isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconLoader2 className="size-3.5 shrink-0 animate-spin text-foreground-secondary" />
        ) : (
          <IconClipboardPlus className="size-3.5 shrink-0 text-foreground-secondary" />
        )}
        <span className={noteButtonLabelClass(inlineCapture)}>
          Insert Current Conditions
        </span>
      </Button>
    )
  }
)
